import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Channel, UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import { debounce } from 'lodash';

import { channelByUser, ChannelOrUserType, isChannel } from './ChatUtils';
import { ResultsDropdown } from './ResultsDrowdow';
import { SearchComponent } from '@/components/common/Search';
import { chatContext } from '@/contexts/ChatContext';

import './ChannelSearch.scss';

export const ChannelSearch = () => {
  const { client, setActiveChannel } = useChatContext();
  const { t } = useTranslation();
  const chatCtx = useContext(chatContext);

  const [allChannels, setAllChannels] = useState<ConcatArray<ChannelOrUserType> | undefined>();
  const [teamChannels, setTeamChannels] = useState<Channel[] | undefined>();
  const [directChannels, setDirectChannels] = useState<UserResponse[] | undefined>();

  const [focused, setFocused] = useState<number>();
  const [focusedId, setFocusedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [showResults, setShowResults] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        setFocused((prevFocused) => {
          if (prevFocused === undefined || allChannels === undefined) return 0;
          return prevFocused === allChannels.length - 1 ? 0 : prevFocused + 1;
        });
      } else if (event.key === 'ArrowUp') {
        setFocused((prevFocused) => {
          if (prevFocused === undefined || allChannels === undefined) return 0;
          return prevFocused === 0 ? allChannels.length - 1 : prevFocused - 1;
        });
      } else if (event.key === 'Enter') {
        event.preventDefault();

        if (allChannels !== undefined && focused !== undefined) {
          const channelToCheck = allChannels[focused];

          if (isChannel(channelToCheck)) {
            setActiveChannel(channelToCheck as Channel);
          } else {
            void channelByUser({ client, setActiveChannel, user: channelToCheck as UserResponse });
          }
        }

        setFocused(undefined);
        setFocusedId('');
      }
    },
    [allChannels, client, focused, setActiveChannel]
  );

  useEffect(() => {
    if (query) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, query]);

  useEffect(() => {
    if (!query) {
      setTeamChannels([]);
      setDirectChannels([]);
    }
  }, [query]);

  useEffect(() => {
    if (focused && focused >= 0 && allChannels) {
      setFocusedId(allChannels[focused]?.id || '');
    }
  }, [allChannels, focused]);

  const setChannel = (channel: Channel) => {
    setQuery(undefined);
    setActiveChannel(channel);
  };

  if (!chatCtx!.chatId) {
    return;
  }

  async function getChannels(text: string) {
    try {
      const channelResponse = client.queryChannels(
        {
          name: { $autocomplete: text },
        },
        {},
        { limit: 5 }
      );

      const [channels] = await Promise.all([channelResponse]);

      setTeamChannels(channels);
      setAllChannels([...channels]);
    } catch (event) {
      console.error(event);
      setQuery(undefined);
    }

    setLoading(false);
  }

  // TODO jo lenne emiatt nem behuzni a lodash-t
  const getChannelsDebounce = debounce(getChannels, 100, {
    trailing: true,
  });

  function onSearch(searchText: string) {
    setLoading(true);
    setFocused(undefined);
    setQuery(searchText);
    if (!searchText) return;
    setShowResults(true);

    void getChannelsDebounce(searchText);
  }

  return (
    <div className="channel-search__container" ref={containerRef}>
      <div className="channel-search__input__wrapper">
        <SearchComponent
          searchText={query}
          setSearchText={onSearch}
          placeholder={t('streamChat.custom.searchPlaceHolder')}
        />
      </div>
      {query && showResults && (
        <ResultsDropdown
          teamChannels={teamChannels}
          directChannels={directChannels}
          focusedId={focusedId}
          loading={loading}
          setChannel={setChannel}
        />
      )}
    </div>
  );
};
